/**
 * screens/candidates/candidateProfile.tsx
 * Candidate profile — wired to GET /api/hire/v2/candidates/:id.
 * Header + consent chip + Renew/Withdraw · summary + skills · extraction
 * confidence · resume versions · contact (PII) · consent ledger.
 * "Top job matches" arrives with the reverse-match slice.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useNavigation, useRoute, useFocusEffect, useIsFocused } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { useWorkspaceStats } from '../../hooks/useAiUsage';
import { ActionButton, StatusChip, Panel, ProgressMeter, Icon } from '../../components/ui';
import { Alert } from '../../components/alert';
import { MiniAvatar, SkillChips } from '../../components/hire/badges';
import { getCandidate, recordConsent, dismissDuplicate, downloadCandidateSourceFile } from '../../services/hire/candidateV2';
import { jobMatches, recordAction } from '../../services/hire/matchV2';
import { eraseCandidate } from '../../services/hire/complianceV2';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import EditCandidatePiiModal from '../../components/hire/editCandidatePiiModal';
import CompareDuplicateModal from '../../components/hire/compareDuplicateModal';
// Edit the extracted data — appends a resume version and re-extracts the profile.
import UpdateResumeModal from '../../components/hire/updateResumeModal';
// Correct the extracted profile in place (no new resume version).
import EditProfileModal from '../../components/hire/editProfileModal';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';
import type { JobMatchesResponse } from '../../schemas/hire/matchV2';
import { SegmentedBar, Modal } from '../../components/ui';
import type { AppStackParamList } from '../../navigation/types';
import { candStatusVM, consentVM } from './candidatesVm';
import type { Tone } from '../../theme/tone';

/** Premium staggered entrance — kept short so nothing feels like it's hanging. */
function rise(delay: number) {
  return web({ animation: 'sk-rise 360ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

type Nav = NativeStackNavigationProp<AppStackParamList>;
type Rt = RouteProp<AppStackParamList, 'candidateDetail'>;

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}
function confTone(v: number): Tone {
  return v >= 0.7 ? 'success' : v >= 0.5 ? 'warn' : 'danger';
}
const CONSENT_EVENT_TONE: Record<string, Tone> = { granted: 'success', renewed: 'success', withdrawn: 'danger', expired: 'warn' };

export default function CandidateProfileScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { isMobile } = useBreakpoints();
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<Rt>();
  const id = params.id;

  const [data, setData] = useState<CandidateDetail | null>(null);
  const [matches, setMatches] = useState<JobMatchesResponse | null>(null);
  const [shortlisted, setShortlisted] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);   // confirm before jumping to the duplicate
  const [notDupOpen, setNotDupOpen] = useState(false); // confirm before clearing the flag
  const [grown, setGrown] = useState(false); // drives the confidence-bar grow-in
  const [loadingResume, setLoadingResume] = useState(false);
  const [downloadingResume, setDownloadingResume] = useState(false);

  // Fetch a signed URL for the stored original resume and open it (scan-gated).
  const onViewResume = async () => {
    setLoadingResume(true);
    // Open the tab synchronously inside the click gesture — a window.open after
    // an await is treated as non-user-initiated and blocked. `noopener` is not
    // usable here (we need the handle to navigate), so `opener` is nulled to keep
    // reverse-tabnabbing shut.
    const win = Platform.OS === 'web' ? (globalThis as any).open?.('about:blank', '_blank') : null;
    if (win) win.opener = null;
    try {
      // Bytes through our own API, shown as a blob URL, rather than sending the
      // browser to the cross-origin SK-Drive signed URL: that URL has no CORS
      // headers, so a slow or refused fetch there left a blank tab with nothing
      // to explain it. This path also reuses the server's cached clean-scan
      // verdict, so View costs one call instead of its own scan + sign round trip.
      const blob = await downloadCandidateSourceFile(id);
      const blobUrl = URL.createObjectURL(blob);
      if (win) win.location.href = blobUrl;
      else if (Platform.OS === 'web') (globalThis as any).open?.(blobUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      if (win) win.close();
      showToast('error', 'Could not open the resume', getApiErrorMessage(e, 'The file may still be scanning, or SK Drive is unreachable. Try again shortly.'));
    } finally {
      setLoadingResume(false);
    }
  };

  // Save the stored resume to disk (scan-gated server-side). The bytes are
  // streamed through our own API rather than the Drive signed URL: that URL is
  // cross-origin with no CORS headers, so the browser cannot read it as a blob
  // and an anchor to it renders the PDF in a tab instead of saving it.
  const onDownloadResume = async () => {
    setDownloadingResume(true);
    try {
      const blob = await downloadCandidateSourceFile(id);
      const name = data?.sourceFilename ?? 'candidate-resume';
      if (Platform.OS === 'web') {
        const doc: any = (globalThis as any).document;
        const blobUrl = URL.createObjectURL(blob);
        const a = doc.createElement('a');
        a.href = blobUrl;
        a.download = name;
        doc.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick — tearing the object URL down synchronously
        // can cancel the download before the browser has read it.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
      }
    } catch (e) {
      showToast('error', 'Could not download the resume', getApiErrorMessage(e, 'The file may still be scanning. Try again shortly.'));
    } finally {
      setDownloadingResume(false);
    }
  };

  const load = useCallback(() => {
    let active = true;
    getCandidate(id).then((d) => { if (active) setData(d); }).catch(() => { if (active) setData(null); });
    jobMatches(id).then((m) => { if (active) setMatches(m); }).catch(() => { if (active) setMatches(null); });
    return () => { active = false; };
  }, [id]);
  useFocusEffect(load);

  /**
   * The résumé profile is derived just AFTER the candidate is created, so a
   * profile opened straight from "Add candidate" arrives without one. The server
   * bumps the workspace revision when it lands, and that arrives here over the
   * existing SSE stream — so re-read the candidate on a bump while this screen is
   * the visible one, and the summary, skills and confidence fill themselves in
   * with no refresh and no polling.
   */
  const isFocused = useIsFocused();
  const { rev } = useWorkspaceStats();
  const lastRev = useRef(rev);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!isFocused) { lastRev.current = rev; return; }
    if (rev === lastRev.current) return;
    lastRev.current = rev;
    loadRef.current();
  }, [rev, isFocused]);

  // Animate the extraction-confidence bars from 0 → target once data lands.
  useEffect(() => {
    setGrown(false);
    const raf = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(raf);
  }, [data?.id]);

  const shortlist = async (requisitionId: string) => {
    try {
      await recordAction({ requisitionId, candidateId: id, action: 'shortlisted' });
      setShortlisted((p) => ({ ...p, [requisitionId]: true }));
      showToast('success', 'Shortlisted', `${data?.name ?? 'Candidate'} was shortlisted for this job.`);
    } catch (e) {
      showToast('error', 'Could not shortlist', getApiErrorMessage(e));
    }
  };

  const doErase = async () => {
    setErasing(true);
    try {
      await eraseCandidate(id);
      setEraseOpen(false);
      load();
      showToast('success', 'Candidate erased', 'The record was crypto-shredded per DPDP.');
    } catch (e) {
      showToast('error', 'Could not erase candidate', getApiErrorMessage(e));
    } finally { setErasing(false); }
  };

  const doConsent = async (action: 'renew' | 'withdraw') => {
    setBusy(true);
    try {
      setData(await recordConsent(id, action));
      showToast('success', action === 'renew' ? 'Consent renewed' : 'Consent withdrawn', action === 'renew' ? 'The consent TTL was extended.' : 'Matching is paused until consent is renewed.');
    } catch (e) {
      showToast('error', action === 'renew' ? 'Could not renew consent' : 'Could not withdraw consent', getApiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const doDismissDup = async () => {
    setDismissing(true);
    try {
      setData(await dismissDuplicate(id));
      showToast('success', 'Marked as not a duplicate', 'The possible-duplicate flag was cleared.');
    } catch (e) {
      showToast('error', 'Could not clear the flag', getApiErrorMessage(e));
    } finally { setDismissing(false); }
  };

  if (!data) return <View style={styles.loading}><ActivityIndicator size="large" color={colors.accent} /></View>;

  const st = candStatusVM(data.status);
  const cs = consentVM(data.consentStatus);
  const p = data.profile;
  const title = (p?.titlesHistory?.[0]?.titleSlug) ?? null;
  const confEntries = p ? Object.entries(p.extractionConfidence) : [];
  // No profile YET (still being read) vs no profile at all — different messages.
  const pending = !p && data.profilePending === true;

  const meta = (label: string, value: string) => (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, gap: 12 }}>
      <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>{label}</Text>
      <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium, textAlign: 'right', flexShrink: 1 }}>{value}</Text>
    </View>
  );

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        <Pressable onPress={() => navigation.navigate('candidates')} style={[{ flexDirection: 'row', alignItems: 'center', gap: 5, alignSelf: 'flex-start' }, rise(0), web({ cursor: 'pointer' })]}>
          <Icon name="chev-l" size={15} color={colors.ink3} />
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>Candidates</Text>
        </Pressable>

        {/* Header */}
        <View style={[styles.header, rise(40), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 16 }]}>
          <View style={{ flexDirection: 'row', gap: 16, alignItems: 'center', minWidth: 0, flex: 1 }}>
            <MiniAvatar name={data.name} size={56} />
            <View style={{ minWidth: 0, flex: 1 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.5, flexShrink: 1 }}>{data.name}</Text>
                <StatusChip tone={st.tone} label={st.label} />
              </View>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 3 }}>
                {[title, p?.currentLocation].filter(Boolean).join(' · ') || '—'} · <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs }}>{data.code}</Text>
              </Text>
              {/* Consent status sits with the identity (it describes the person),
                  not mixed into the action buttons. */}
              <View style={{ flexDirection: 'row', marginTop: 9 }}>
                <StatusChip tone={cs.tone} label={cs.label} />
              </View>
            </View>
          </View>
          {data.status !== 'erased' && (
            isMobile ? (
              // Mobile: Edit + consent action as an even pair, destructive Erase
              // on its own full-width row so it's clearly set apart.
              <View style={{ alignSelf: 'stretch', gap: 8 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <View style={{ flex: 1 }}><ActionButton label="Edit" variant="secondary" icon="edit" onPress={() => setEditOpen(true)} /></View>
                  <View style={{ flex: 1 }}>
                    {data.consentStatus === 'active'
                      ? <ActionButton label="Withdraw" variant="secondary" onPress={() => doConsent('withdraw')} loading={busy} />
                      : <ActionButton label="Renew consent" variant="success" onPress={() => doConsent('renew')} loading={busy} />}
                  </View>
                </View>
                <ActionButton label="Erase (DPDP)" variant="danger" onPress={() => setEraseOpen(true)} />
              </View>
            ) : (
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <ActionButton label="Edit" variant="secondary" size="sm" icon="edit" onPress={() => setEditOpen(true)} />
                {data.consentStatus !== 'active' && <ActionButton label="Renew consent" variant="success" size="sm" onPress={() => doConsent('renew')} loading={busy} />}
                {data.consentStatus === 'active' && <ActionButton label="Withdraw" variant="secondary" size="sm" onPress={() => doConsent('withdraw')} loading={busy} />}
                <ActionButton label="Erase (DPDP)" variant="danger" size="sm" onPress={() => setEraseOpen(true)} />
              </View>
            )
          )}
        </View>

        {/* Advisory duplicate flag (CC-002 §16) — never blocks, never auto-merges. */}
        {data.possibleDuplicateOf && data.status !== 'erased' && (
          <View style={rise(60)}>
            <Alert tone="warn" title="Possible duplicate">
              <Text style={{ fontSize: fontSize.sm, color: colors.ink2, marginBottom: 10 }}>
                {data.duplicateFlagReason || 'Same name and current employer as an existing candidate.'}
                {data.duplicateOf ? `  ·  ${data.duplicateOf.name} (${data.duplicateOf.code})` : ''}
              </Text>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                {data.duplicateOf && (
                  <ActionButton
                    label="Review & merge"
                    variant="secondary"
                    size="sm"
                    onPress={() => setMergeOpen(true)}
                  />
                )}
                <ActionButton label="Not a duplicate" variant="secondary" size="sm" loading={dismissing} onPress={() => setNotDupOpen(true)} />
              </View>
            </Alert>
          </View>
        )}

        {/* Resume document — the original uploaded resume (PDF/CSV/Excel) in SK-Drive. */}
        {data.sourceFileId && data.status !== 'erased' && (
          <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card }, rise(70), web({ boxShadow: '0 1px 0 rgba(16,24,40,0.04)' })]}>
            <Pressable
              onPress={onDownloadResume}
              disabled={downloadingResume}
              accessibilityLabel="Download the resume on file"
              style={[{ width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }, web({ cursor: 'pointer', transition: 'background 140ms ease, border-color 140ms ease' })]}
            >
              {downloadingResume ? <ActivityIndicator size="small" color={colors.ink2} /> : <Icon name="download" size={18} color={colors.ink2} />}
            </Pressable>
            <View style={{ flex: 1, minWidth: 0 }}>
              {/* "Resume on file", not "Resume document": the label is uppercased
                  and letter-spaced, and accented capitals (RESUME) sit badly in
                  that treatment. "on file" also says something the filename does
                  not — this is the stored original, not a re-render. */}
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: fontWeight.semibold }}>Resume on file</Text>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium, marginTop: 2 }} numberOfLines={1}>{data.sourceFilename ?? 'Original resume'}</Text>
            </View>
            <ActionButton label="View" variant="secondary" size="sm" icon="eye" onPress={onViewResume} loading={loadingResume} />
          </View>
        )}

        {/* Edit contact details (name/email/phone). */}
        {/* In-place correction of the current profile — no new version. */}
        <EditProfileModal
          open={profileOpen}
          candidate={data}
          onClose={() => setProfileOpen(false)}
          onUpdated={(c) => { setProfileOpen(false); setData(c); }}
        />

        {/* Edit extracted data → append a resume version and re-extract. This
            writes a new current profile, which is exactly what marks any match
            ranking containing this candidate as out of date. */}
        <UpdateResumeModal
          open={resumeOpen}
          candidate={data}
          onClose={() => setResumeOpen(false)}
          onUpdated={(c) => { setResumeOpen(false); setData(c); }}
        />

        <EditCandidatePiiModal
          open={editOpen}
          candidate={data}
          onClose={() => setEditOpen(false)}
          onUpdated={(c) => { setEditOpen(false); setData(c); }}
        />

        <Modal
          open={eraseOpen}
          onClose={() => { if (!erasing) setEraseOpen(false); }}
          title="Erase candidate (DPDP)"
          size="sm"
          footer={(
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <ActionButton label="Cancel" variant="secondary" onPress={() => setEraseOpen(false)} />
              <ActionButton label="Erase permanently" variant="danger" onPress={doErase} loading={erasing} />
            </View>
          )}
        >
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, lineHeight: 20 }}>
            This is the sanctioned destructive path: the envelope key is destroyed and all embeddings, summaries and the email hash are nulled. Prior match-run scores remain (anonymized). This cannot be undone.
          </Text>
        </Modal>

        {/* Side-by-side comparison of the flagged pair — replaces jumping away
            to the other profile, which made you compare from memory. */}
        <CompareDuplicateModal
          open={mergeOpen}
          leftId={data.id}
          rightId={data.duplicateOf?.id ?? null}
          onClose={() => setMergeOpen(false)}
          onOpenCandidate={(cid) => navigation.navigate('candidateDetail', { id: cid })}
          onDismissFlag={() => { setMergeOpen(false); setNotDupOpen(true); }}
          dismissing={dismissing}
        />

        {/* Confirm before clearing the duplicate flag. */}
        <Modal
          open={notDupOpen}
          onClose={() => { if (!dismissing) setNotDupOpen(false); }}
          title="Mark as not a duplicate?"
          size="sm"
          footer={(
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <ActionButton label="Cancel" variant="secondary" onPress={() => setNotDupOpen(false)} />
              <ActionButton
                label="Confirm"
                variant="primary"
                loading={dismissing}
                onPress={async () => { await doDismissDup(); setNotDupOpen(false); }}
              />
            </View>
          )}
        >
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, lineHeight: 20 }}>
            This clears the possible-duplicate flag on {data.name}{data.duplicateOf ? ` against ${data.duplicateOf.name} (${data.duplicateOf.code})` : ''}. The banner won't show again for this pairing.
          </Text>
        </Modal>

        <View style={[{ flexDirection: 'row', gap: 16 }, isMobile && { flexDirection: 'column' }]}>
          {/* Left */}
          <View style={{ flex: isMobile ? undefined : 1.4, gap: 16 }}>
            <Panel
              title="Summary · ~150 tokens"
              style={rise(70)}
              // The summary and skills are EXTRACTED, not typed — so "edit" means
              // supplying a newer resume and re-extracting from it. Versions are
              // append-only: the current profile is replaced, history is kept.
              action={
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {/* Fix a bad extraction in place… */}
                  <ActionButton label="Edit" variant="secondary" size="sm" icon="edit" onPress={() => setProfileOpen(true)} />
                  {/* …or supply a genuinely newer resume, which appends a version. */}
                  <ActionButton label="New resume" variant="secondary" size="sm" icon="upload" onPress={() => setResumeOpen(true)} />
                </View>
              }
            >
              {/* The candidate is saved the moment you press Add; the résumé is
                  read just after. Say that, instead of showing "No summary
                  available" — which reads as a failed extraction rather than one
                  that is seconds away. It replaces itself when the profile lands
                  (the workspace revision bumps over SSE). */}
              {pending ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <ActivityIndicator size="small" color={colors.accent} />
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink3, lineHeight: 20, flex: 1 }}>
                    Reading the résumé — skills, experience and the summary appear here in a few seconds.
                    The candidate is already saved.
                  </Text>
                </View>
              ) : (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink2, lineHeight: 20 }}>{p?.summaryCompressed || 'No summary available.'}</Text>
              )}
              {!!p?.skills.length && (
                <View style={{ marginTop: 12 }}><SkillChips skills={p.skills.map((s) => s.slug)} max={16} tone="accent" /></View>
              )}
            </Panel>

            <Panel title="Extraction confidence" style={rise(110)}>
              {confEntries.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink4 }}>No profile extracted.</Text>
              ) : (
                <View style={{ gap: 12 }}>
                  {confEntries.map(([field, v]) => (
                    <View key={field} style={{ gap: 5 }}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textTransform: 'capitalize' }}>{field}</Text>
                        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{Math.round(v * 100)}%</Text>
                      </View>
                      <ProgressMeter pct={(grown ? v : 0) * 100} tone={confTone(v)} height={7} />
                    </View>
                  ))}
                </View>
              )}
            </Panel>

            <Panel title="Resume versions · append-only" style={rise(150)}>
              {data.resumeVersions.map((r, i) => (
                <View key={r.id} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: i === data.resumeVersions.length - 1 ? 0 : 1, borderBottomColor: colors.line }}>
                  <View>
                    <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }}>v{r.version}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>{fmtDate(r.createdAt)} · {r.contentHash}</Text>
                  </View>
                  {r.isCurrent ? <StatusChip tone="accent" label="current" dot={false} /> : null}
                </View>
              ))}
            </Panel>
          </View>

          {/* Right */}
          <View style={{ flex: isMobile ? undefined : 1, gap: 16 }}>
            {/* Titled "Contact · PII (encrypted at rest)" until now, which was not
                true: lib/pii on the API is still the documented stub — sealPii
                writes the name, email and phone as UTF-8 JSON under the marker
                ref `stub:plaintext-json`, pending the key-envelope client. The
                backend is candid about that in its own comments; this label was
                the one place the product asserted otherwise, to the person least
                able to check it and about someone else's personal data. Restore
                the claim when envelope encryption actually ships. */}
            <Panel title="Contact · personal data" style={rise(150)}>
              {meta('Email', data.email ?? '—')}
              {meta('Phone', data.phone ?? '—')}
              {meta('Experience', p?.totalExperienceYears != null ? `${p.totalExperienceYears} y` : '—')}
              {meta('Notice', p?.noticePeriodDays != null ? `${p.noticePeriodDays} d` : '—')}
              {meta('Remote OK', p?.remoteOk ? 'Yes' : 'No')}
            </Panel>

            <Panel title="Consent ledger" style={rise(130)}>
              {data.consentEvents.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink4 }}>No consent events.</Text>
              ) : (
                data.consentEvents.map((e, i) => {
                  const isLast = i === data.consentEvents.length - 1;
                  const dotColor = colors[(CONSENT_EVENT_TONE[e.eventType] ?? 'neutral')];
                  return (
                    <View key={e.id} style={{ flexDirection: 'row', gap: 13 }}>
                      {/* Timeline rail: event dot + connector to the next event. */}
                      <View style={{ alignItems: 'center', width: 10 }}>
                        <View style={{ width: 10, height: 10, borderRadius: 999, marginTop: 4, backgroundColor: dotColor, ...(Platform.OS === 'web' ? ({ boxShadow: `0 0 0 3px ${colors.paper2}` } as object) : {}) }} />
                        {!isLast && <View style={{ flex: 1, width: 1.5, marginTop: 6, marginBottom: 2, backgroundColor: colors.line }} />}
                      </View>
                      <View style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 14 }}>
                        <Text style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold, textTransform: 'capitalize', letterSpacing: -0.1 }}>{e.eventType} · {e.purpose}</Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 4 }}>{fmtDate(e.recordedAt)}{e.ttlExpiresAt ? ` · TTL ${fmtDate(e.ttlExpiresAt)}` : ''}</Text>
                      </View>
                    </View>
                  );
                })
              )}
            </Panel>

            <Panel title="Top job matches" style={rise(170)}>
              {matches == null ? (
                <ActivityIndicator color={colors.accent} />
              ) : matches.blocked ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>
                  {matches.reason === 'consent_inactive'
                    ? 'Matching disabled — consent is not active. Renew consent to see job matches.'
                    : 'No extracted profile yet — add a resume to enable matching.'}
                </Text>
              ) : matches.matches.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>No qualifying open jobs right now.</Text>
              ) : (
                <View>
                  {matches.matches.map((jm, i) => (
                    <View key={jm.requisitionId} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.line }}>
                      <Text style={{ width: 20, fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: colors.ink3 }}>{i + 1}</Text>
                      <Pressable onPress={() => navigation.navigate('jobDetail', { id: jm.requisitionId })} style={[{ flex: 1.4, minWidth: 0 }, web({ cursor: 'pointer' })]}>
                        <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink, fontWeight: fontWeight.medium }}>{jm.title}</Text>
                        <Text style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink4 }}>{jm.code} · cosine {jm.cosine.toFixed(3)}</Text>
                      </Pressable>
                      <Text style={{ width: 48, fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{jm.score}</Text>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <SegmentedBar segments={[
                          { value: jm.breakdown.niceToHave, tone: 'purple' },
                          { value: jm.breakdown.experience, tone: 'info' },
                          { value: jm.breakdown.title, tone: 'teal' },
                          { value: jm.breakdown.tenure, tone: 'warn' },
                          { value: jm.breakdown.recency, tone: 'neutral' },
                        ]} max={100} />
                      </View>
                      <View style={{ width: 96, alignItems: 'flex-end' }}>
                        {jm.pipelineStage ? (
                          <StatusChip tone="info" label={jm.pipelineStage.replace(/_/g, ' ')} />
                        ) : shortlisted[jm.requisitionId] ? (
                          <StatusChip tone="success" label="✓ shortlisted" dot={false} />
                        ) : (
                          <ActionButton label="+ Shortlist" variant="secondary" size="sm" onPress={() => void shortlist(jm.requisitionId)} />
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </Panel>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  loading: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
});
